import { runInAction } from "mobx";
import { inject, observer } from "mobx-react";
import React, { useEffect } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import Box from "react-raster";
import { match } from "react-router";
import {
  Avatar,
  AvatarUpload,
  Button,
  Heading,
  Icon,
  Input, InputForm,
  Link,
  Paper,
  Spacer, Text, TextArea
} from "src/components";
import { SideMenu } from "src/components/sideMenu";
import { theme } from "src/config";
import { User } from "src/models";
import { MatchParams } from "src/routes";
import { AuthStore, RoutingStore, UserStore } from "src/stores";
import { IUser } from "src/types";
import styled from "styled-components";

const AvatarContainer = styled.div`
  position:        relative;
  margin-top:      1rem;
  margin-bottom:   1rem;
  justify-content: left;
  display:         flex;
  flex-direction:  column;
  gap:             1rem;
  a {
    margin-left: 2.5rem;
  }
`;

const EditButton = styled.button`
  top:           4rem;
  left:          4rem;
  position:      absolute;
  border-radius: 50%;
  border:        1px solid ${theme.color.white};
  width:         2.25rem;
  height:        2.25rem;
  cursor:        pointer;
  background: ${theme.color.buttons.primary.background};
    :hover {
      background: ${theme.color.buttons.primary.hover};
    }
    :focus {
      outline: 2px solid ${theme.color.buttons.primary.focus};
      -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
    }
    :active {
      background: ${theme.color.buttons.primary.active};
    }
`;

export interface IUserSettingsProps {
  authStore: AuthStore;
  userStore: UserStore;
  routingStore: RoutingStore;
  match: match<MatchParams>; // This is injected so it has to be called match
}
const settings_props: (keyof IUser)[] = [
  "first_name", 
  "email", 
  "last_name", 
  "default_initials", 
  "default_title", 
  "phone", 
  "company_name", 
  "description"
];

export const UserSettings = inject("authStore", "routingStore", "userStore")(observer((props: IUserSettingsProps): JSX.Element => {
    const { t, i18n } = useTranslation();
    const { authStore, userStore } = props;
    const { getAvatar: avatar } = authStore.getCurUser;
    const user: User = userStore.getActive;

    const [userData, setUserData] = React.useState<Partial<IUser>>({});
    const [loading, setLoading] = React.useState<boolean>(false);
    const [touched, setTouched] = React.useState<boolean>(false);
    const [showUploadAvatar, setShowUploadAvatar] = React.useState<boolean>(false);
    const hasChanged = !settings_props.reduce((res: boolean, val: keyof IUser) => {
      return res && user && userData && (user[val] || "") === (userData[val] || "")
    }, true);

    useEffect(() => {
      userStore.setActive(authStore.getCurUser);
      setUserData(authStore.getCurUser)
      return () => {
        userStore.setActive(undefined);
      };
    }, []);

    const editAvatar = () => {
      setShowUploadAvatar(true);
      setTouched(true);
    };

    const discard = () => {
      userStore.setActive(authStore.getCurUser);
      setTouched(false);
      setShowUploadAvatar(false);
    };

    const submit = async () => {
      const user: User = userStore.getActive;
      setLoading(true);
      const result = await user!.save(userData);
      setLoading(false);
      setTouched(false);
      setShowUploadAvatar(false);
      toast.success("user_settings_saved");
    };

    if (!user) return <></>;

    const inputs: { 
      value?:     string;
      onChange?:  (value?:string) => void,
      type?:      string,
      label?:     string,
      id?:        string,
      key?:       string,
      maxLength?: string,
      required?:  boolean,
    }[] = [
      {
        type:     "text",
        key:      "email",
        label:    t("email"),
        id:       "email",
        value:    userData.email || ""
      },{
        type:     "text",
        key:      "first-name",
        label:    t("first_name"),
        required: true,
        id:       "first-name",
        value:    userData.first_name || "",
        onChange: (value: string) => setUserData({...userData, first_name: value})

      },{
        type:     "text",
        key:      "last-name",
        label:    t("last_name"),
        required: true,
        id:       "last-name",
        value:    userData.last_name || "",
        onChange: (value: string) => setUserData({...userData, last_name: value})
      },{
        type:      "text",
        key:       "initials",
        label:     t("initials"),
        required:  true,
        id:        "initials",
        value:     userData.default_initials || "",
        maxLength: "3",
        onChange: (value: string) => setUserData({...userData, default_initials: value})
      },{
        key:      "title",
        type:     "text",
        label:    t("title"),
        required: true,
        id:       "title",
        value:    userData.default_title || "",
        onChange: (value: string) => setUserData({...userData, default_title: value})
      },{
        key:      "phone",
        type:     "text",
        label:    t("phone"),
        required: true,
        id:       "phone",
        value:    userData.phone || "",
        onChange: (value: string) => setUserData({...userData, phone: value})
      },{
        key:      "company",
        type:     "text",
        label:    t("company_name"),
        required: true,
        id:       "company_name",
        value:    userData.company_name || "",
        onChange: (value: string) => setUserData({...userData, company_name: value})
      }
    ];

    return (
      <Paper
        display        = "grid"
        justifyContent = "center"
        colspan        = {10}
        flexDirection  = "row"
        style          = {theme.paper.regular}
        $center
        $noStyling
      >
        <Box display="flex" cols={1}>
          <SideMenu />
        </Box>
        <Box marginLeft="1rem" display="flex" cols={9}>
          <Paper $noShadow width="100%">
            <Box gridRowGap={theme.layout.gutter} alignItems="left">
              <Box
                display        = "flex"
                justifyContent = "flex-start"
                alignItems     = "center"
                flexDirection  = "row"
              >
                <Avatar
                  size     = "large"
                  image    = {user.getAvatar}
                  initials = {user.default_initials}
                  onClick  = {() => {}}
                  context  = "usersettings"
                />
                <Spacer />
                <Heading type="h3">{t("user_settings")}</Heading>
              </Box>
              <InputForm flexDirection="row" alignItems="flex-end">
                <Text size="large" weight="bold">{t("user")}:</Text>
                <Spacer size="xxsmall"/>
                <Link
                  size    = "default"
                  onClick = {() => props.routingStore.push(`/profile/${user.id}`)}
                >
                  {authStore.getCurUser.email}
                </Link>
              </InputForm>
              <InputForm gap="1rem" onChange={() => setTouched(true)}>
                {inputs.map((i) => (
                  <Input
                    key       = {i.key}
                    type      = {i.type}
                    label     = {i.label}
                    id        = {i.id}
                    value     = {i.value}
                    onChange  = {i.onChange}
                    onKeyDown = {submit}
                    required  = {i.required}
                    maxLength = {i.maxLength ? Number(i.maxLength) : undefined}
                  />
                ))}
                <Spacer />
                <TextArea
                  maxLength    = {500}
                  rows         = {10}
                  key          = "about_me"
                  label        = {t("about_me")}
                  defaultValue = {user.description || ""}
                  onChange     = {(value) => user.set("description", value)}
                />
              </InputForm>
              <Box>
                <Text size={"small"} color={theme.font.color.placeholder}>{`(*) ${t("required_fields")}`}</Text>
              </Box>
              <InputForm marginBottom="1rem" gap="0rem">
                <Text size="large" weight="bold">{t("profile_image")}</Text>
                <hr />
                {!!user.getAvatar && !showUploadAvatar ? (
                  <AvatarContainer>
                    <Avatar image={user.getAvatar} size="xlarge" onClick={editAvatar} />
                    <EditButton onClick={async () => {
                      try {
                        await user.set('avatar', null, true);
                        toast.success(t("avatar_remove"));
                      } catch(err) {
                        toast.error(t("avatar_remove_fail"));
                      }
                    }}>
                      <Icon
                        size   = "small"
                        color  = {theme.color.white}
                        iconId = "remove"
                      />
                    </EditButton>
                  </AvatarContainer>
                ) : (
                  <AvatarUpload
                    onCancel = {user.avatar ? 
                      () => {
                        setShowUploadAvatar(false);
                        setTouched(false);
                      } : undefined
                    }
                    onSave={(uri, size, mime) =>
                      runInAction(() => {
                        user.setAvatar(uri, size, mime);
                        setShowUploadAvatar(false);
                        toast.success(t("user_avatar_saved"));
                      })
                    }
                  />
                )}
              </InputForm>
              <InputForm
                display        = "flex"
                justifyContent = "space-between"
                flexDirection  = "row"
              >
                <Button variant="primary" onClick={submit} isLoading={loading} disabled={!hasChanged}>
                  {t("save")}
                </Button>

                {touched && (
                  <Button
                    variant   = "outlined"
                    onClick   = {discard}
                    isLoading = {loading}
                  >
                    {t("undo")}
                  </Button>
                )}
              </InputForm>
            </Box>
          </Paper>
        </Box>
      </Paper>
    );
  })
);
